import React, { useMemo, useState } from 'react';
import { WorkspaceData } from '../types';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@material-ui/core';
import { ComponentEntity } from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Skeleton } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import { WorkspacesService } from '../../../../../services/WorkspacesService';
import { Environment, SystemWorkspace } from '../../../../workspaces/types';

export const NewWorkspaceCard = ({
  env,
  handleNewWorkspaces,
}: {
  env: string;
  handleNewWorkspaces: (workspace: WorkspaceData[]) => void;
}) => {
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const authApi = useApi(microsoftAuthApiRef);
  const { entity } = useEntity<ComponentEntity>();
  const [checked, setChecked] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, authApi, discoveryApi, catalogApi),
    [fetchApi, authApi, discoveryApi, catalogApi],
  );

  const formatWorkspace = (workspace: SystemWorkspace): WorkspaceData => {
    const option = WorkspacesService.toWorkspaceOption(workspace);
    return {
      workspace: option.tenant,
      account: {
        name: option.name,
      },
      enabled: true,
    };
  };

  const getFormatedEnvironment = (): Environment => {
    if (env === 'PRD') {
      return 'production';
    } else if (env === 'STG') {
      return 'staging';
    }
    return 'development';
  };

  const { value: systemWorkspaces } = useAsync(async () => {
    const systemId = WorkspacesService.getSystemId(entity);
    if (!systemId) {
      return [];
    }
    return workspacesService.getAllWorkspacesBySystem(
      getFormatedEnvironment(),
      systemId,
    );
  }, [workspacesService, entity, env]);

  const filteredWorkspaces = useMemo(() => {
    if (!systemWorkspaces) {
      return undefined;
    }
    const term = searchTerm.trim().toLowerCase();
    const matches = term
      ? systemWorkspaces.filter(
          workspace =>
            workspace.name?.toLowerCase().includes(term) ||
            workspace.subdomain?.toLowerCase().includes(term) ||
            workspace.racId?.toLowerCase().includes(term),
        )
      : systemWorkspaces;
    return matches.map(item => formatWorkspace(item));
  }, [systemWorkspaces, searchTerm]);

  function handleToggleWorkspace(value: WorkspaceData) {
    setChecked(prevChecked => {
      if (value.workspace) {
        const currentIndex = prevChecked.indexOf(value.workspace);
        const newChecked = [...prevChecked];

        if (currentIndex === -1) {
          newChecked.push(value.workspace);
        } else {
          newChecked.splice(currentIndex, 1);
        }
        return newChecked;
      }
      return prevChecked;
    });
  }

  const handleAddWorkspaces = () => {
    if (systemWorkspaces)
      handleNewWorkspaces(
        systemWorkspaces
          .map(item => formatWorkspace(item))
          .filter(workspace => checked.includes(workspace.workspace || '')),
      );
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gridRowGap={16}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          fullWidth
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box
          height={400}
          width={500}
          style={{ overflow: 'auto', scrollbarWidth: 'thin' }}
        >
          {filteredWorkspaces &&
            filteredWorkspaces.map(
              value =>
                value.workspace && (
                  <ListItem
                    key={value.workspace}
                    dense
                    button
                    onClick={() => handleToggleWorkspace(value)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checked.indexOf(value.workspace) !== -1}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={value.workspace}
                      primary={value.account?.name}
                      secondary={value.workspace}
                    />
                  </ListItem>
                ),
            )}
          {filteredWorkspaces && filteredWorkspaces.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <span>No workspaces found</span>
            </Box>
          )}
          {!filteredWorkspaces && (
            <Box
              display="flex"
              flexDirection="column"
              gridRowGap={8}
              height="100%"
              marginLeft={2}
              marginTop={1}
            >
              {[1, 2, 3, 4, 5, 6, 7].map(() => (
                <Box display="flex" gridColumnGap={40} alignItems="center">
                  <Skeleton variant="rect" width={20} height={20} />
                  <Skeleton variant="rect" width={400} height={50} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddWorkspaces}
            disabled={checked.length === 0}
          >
            Add Workspaces
          </Button>
        </DialogActions>
      </Box>
    </>
  );
};
